import { supabase } from './supabase'

/**
 * Seul point d'acces au stockage des documents.
 *
 * Tout ce qui touche au bucket passe par ce module : chemins, televersement,
 * suppression, URL de telechargement. Changer de fournisseur de stockage plus
 * tard ne doit toucher que ce fichier.
 *
 * Le bucket est prive : il n'existe aucune URL publique. Un telechargement
 * passe par une URL signee de courte duree, generee a la demande.
 */

export const BUCKET_DOSSIERS = 'dossiers'

export const CATEGORIES_FICHIER = ['cv_origine', 'cv_pdf', 'cv_docx'] as const

export type CategorieFichier = (typeof CATEGORIES_FICHIER)[number]

/**
 * Duree de validite d'une URL de telechargement.
 *
 * Courte volontairement : l'URL signee contourne RLS le temps de sa validite,
 * elle est donc traitee comme un secret a duree de vie minimale. Elle est
 * generee au clic, pas au chargement de la page.
 */
export const DUREE_URL_SIGNEE_SECONDES = 60

export class ErreurStockage extends Error {
  /** Erreur rendue par le fournisseur de stockage, conservee telle quelle. */
  override readonly cause: unknown

  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'ErreurStockage'
    this.cause = cause
  }
}

/**
 * Chemin canonique d'un document dans le bucket : `{dossier_id}/{categorie}`.
 *
 * Ni nom de fichier, ni extension. Le chemin est donc deterministe, et un
 * remplacement ecrase toujours l'objet precedent : aucun orphelin ne peut
 * s'accumuler dans le bucket.
 *
 * La meme regle est gravee cote base dans la contrainte
 * `fichiers_chemin_canonique` (migration 0001). Les deux doivent rester
 * identiques : une regle que seul le front applique n'est pas une regle.
 */
export function cheminFichier(dossierId: string, categorie: CategorieFichier): string {
  return `${dossierId}/${categorie}`
}

/**
 * URL signee de telechargement.
 *
 * `nomFichier` ne sert qu'au Content-Disposition : c'est le nom que verra
 * l'utilisateur, sans aucun effet sur le chemin de stockage.
 */
export async function urlSigneeTelechargement(
  dossierId: string,
  categorie: CategorieFichier,
  nomFichier: string,
  dureeSecondes: number = DUREE_URL_SIGNEE_SECONDES,
): Promise<string> {
  const chemin = cheminFichier(dossierId, categorie)

  const { data, error } = await supabase.storage
    .from(BUCKET_DOSSIERS)
    .createSignedUrl(chemin, dureeSecondes, { download: nomFichier })

  if (error) {
    throw new ErreurStockage(`URL de telechargement indisponible pour ${chemin}.`, error)
  }
  if (!data?.signedUrl) {
    throw new ErreurStockage(`URL de telechargement vide pour ${chemin}.`)
  }

  return data.signedUrl
}

/**
 * Televerse ou remplace un document.
 *
 * `upsert` est volontaire : le chemin etant deterministe, un nouveau
 * televersement dans la meme categorie remplace l'objet en place.
 */
export async function televerser(
  dossierId: string,
  categorie: CategorieFichier,
  contenu: Blob | File,
  typeMime: string,
): Promise<string> {
  const chemin = cheminFichier(dossierId, categorie)

  const { error } = await supabase.storage
    .from(BUCKET_DOSSIERS)
    .upload(chemin, contenu, { contentType: typeMime, upsert: true })

  if (error) {
    throw new ErreurStockage(`Televersement refuse pour ${chemin}.`, error)
  }

  return chemin
}

/** Supprime l'objet de stockage d'un document. */
export async function supprimer(
  dossierId: string,
  categorie: CategorieFichier,
): Promise<void> {
  const chemin = cheminFichier(dossierId, categorie)

  const { error } = await supabase.storage.from(BUCKET_DOSSIERS).remove([chemin])

  if (error) {
    throw new ErreurStockage(`Suppression refusee pour ${chemin}.`, error)
  }
}

/**
 * Telecharge un document, sans jamais exposer l'URL signee.
 *
 * L'URL est generee au clic, vit 60 secondes, sert une fois, et n'apparait ni
 * dans la barre d'adresse ni dans l'historique : le contenu est recupere puis
 * remis au navigateur sous forme d'objet local, revoque aussitot. Une URL signee
 * contourne la RLS le temps de sa validite — elle se traite comme un secret a
 * duree de vie minimale.
 */
export async function telechargerDocument(
  dossierId: string,
  categorie: CategorieFichier,
  nomFichier: string,
): Promise<void> {
  const url = await urlSigneeTelechargement(dossierId, categorie, nomFichier)

  const reponse = await fetch(url)
  if (!reponse.ok) {
    throw new ErreurStockage(
      `Telechargement refuse pour ${nomFichier} (${reponse.status}).`,
    )
  }

  const contenu = await reponse.blob()
  const urlLocale = URL.createObjectURL(contenu)

  const lien = document.createElement('a')
  lien.href = urlLocale
  lien.download = nomFichier
  lien.rel = 'noopener'
  document.body.appendChild(lien)
  lien.click()
  lien.remove()

  // La revocation est DIFFEREE, et ce n'est pas un detail : revoquer dans la
  // foulee du clic annule le telechargement avant que le navigateur ait
  // commence a lire l'objet. Le bouton semble alors ne rien faire — le pire
  // comportement possible en entretien. Sans revocation du tout, l'objet
  // resterait en memoire jusqu'au rechargement de la page.
  setTimeout(() => URL.revokeObjectURL(urlLocale), DELAI_REVOCATION_MS)
}

/** Laisse au navigateur le temps d'ouvrir l'objet avant de le liberer. */
const DELAI_REVOCATION_MS = 60_000

/**
 * URL signee pour un APERCU, sans forcer le telechargement.
 *
 * La difference avec urlSigneeTelechargement tient a l'option `download`, qui
 * pose un Content-Disposition en piece jointe. Deux usages, deux appels : une
 * URL d'apercu servie en piece jointe declencherait un enregistrement de
 * fichier au lieu d'un affichage.
 */
export async function urlSigneeApercu(
  dossierId: string,
  categorie: CategorieFichier,
  dureeSecondes: number = DUREE_URL_SIGNEE_SECONDES,
): Promise<string> {
  const chemin = cheminFichier(dossierId, categorie)

  const { data, error } = await supabase.storage
    .from(BUCKET_DOSSIERS)
    .createSignedUrl(chemin, dureeSecondes)

  if (error) {
    throw new ErreurStockage(`URL d'apercu indisponible pour ${chemin}.`, error)
  }
  if (!data?.signedUrl) {
    throw new ErreurStockage(`URL d'apercu vide pour ${chemin}.`)
  }

  return data.signedUrl
}

/**
 * Prepare un apercu : rend une URL LOCALE, a afficher dans la page.
 *
 * L'URL signee est generee au clic, consommee ici, et n'entre jamais dans le
 * DOM ni dans la barre d'adresse : ce qui circule ensuite est un objet local,
 * que `libererApercu` revoque a la fermeture. Une URL signee contourne la RLS
 * le temps de sa validite — elle se traite comme un secret a duree de vie
 * minimale.
 */
export async function apercuPdf(
  dossierId: string,
  categorie: CategorieFichier,
): Promise<string> {
  const url = await urlSigneeApercu(dossierId, categorie)

  const reponse = await fetch(url)
  if (!reponse.ok) {
    throw new ErreurStockage(`Apercu refuse pour ${cheminFichier(dossierId, categorie)} (${reponse.status}).`)
  }

  const contenu = await reponse.blob()
  return URL.createObjectURL(contenu)
}

/** Revoque l'objet local d'un apercu. A appeler a la fermeture, sans exception. */
export function libererApercu(urlLocale: string): void {
  URL.revokeObjectURL(urlLocale)
}

/** Seul type de document que le navigateur sait afficher tel quel. */
export const TYPE_APERCU_POSSIBLE = 'application/pdf'

/**
 * Un document est-il previsualisable ?
 *
 * La decision se prend sur le TYPE MIME enregistre en base, jamais sur
 * l'extension du nom de fichier : le CV d'origine peut arriver en .docx, et
 * c'est l'Edge Function qui a etabli son type reel a partir de ses octets — pas
 * de son nom, qui vient de l'appelant.
 */
export function estPrevisualisable(typeMime: string | null | undefined): boolean {
  return typeMime === TYPE_APERCU_POSSIBLE
}
